import { useGetViewJobRelevantJobsQuery } from './__generated__/findViewJobRelevantJobs.gqlgenerated';
import { ViewJobDislikedJobCard } from './ViewJobDislikedJobCard';
import { useSetVanishingBottomToastPropsContext } from '../../../../../../ace/components/VanishingBottomToast/SetVanishingBottomToastPropsContext';
import { JobCard, type JobCardData } from '../../../../../../jobcard/components/JobCard/JobCard';
import { DislikedJobsProvider } from '../../../../../../jobcard/context/DislikedJobsContext';
import {
    ArrowLeft,
    ArrowRight,
    Box,
    Button,
    Heading,
    ScrollView,
    type ScrollViewRef,
    Text
} from '../../../../../../mads';
import { useI18n } from '../../../../../../shared/I18n';
import {
    JSTLandingScreen,
    JSTViewJobTarget,
} from '../../../../../../shared/native-modules/tare/JSTLandingScreen';
import navigateTo from '../../../../../../shared/native-modules/tare/navigateTo';
import { prepareJobClickUrl } from '../../../../../../shared/util/urlUtils';
import { useViewjobProctorAllocation } from '../../../../../proctor/__generated__/ViewjobProctorContext';
import { MobVjCompanyConquestingTst } from '../../../../../proctor/__generated__/ViewjobProctorSpecifications';
import { useViewjobOneGraphModel } from '../../../../context/ViewJobDataContext';
import { useIsDesktop } from '../../../../context/ViewJobScreenDisplay';
import { ViewJobDivider } from '../../Divider/ViewJobDivider';
import { JobCardLoadingIndicator } from '../../LoadingIndicator/LoadingIndicator';

import { colors } from '@indeed/ifl-tokens';
import {
    AvroOptional,
    log as logReceivedFlag,
} from '@indeed/signals-schema/com/indeed/signals/schema/profile/MobRcvFlag';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Platform,
    StyleSheet,
    type LayoutChangeEvent,
    type NativeScrollEvent,
    type NativeSyntheticEvent,
} from 'react-native';

const CARD_WIDTH = 320;
/** Box wrapper around each card adds marginRight 8 + paddingRight 16 between card starts. */
const CARD_STRIDE = CARD_WIDTH + 24;

/** Maps proctor bucket values to the number of competitor jobs to fetch */
const BUCKET_TO_JOB_LIMIT: Record<number, number> = {
    3: 3,
    4: 15,
    5: 30,
};

export function CompanyConquestingJobCards({
    jobKey,
    loggedIn,
}: Readonly<{
    jobKey: string;
    loggedIn: boolean;
}>) {
    const setVanishingBottomToastProps = useSetVanishingBottomToastPropsContext();
    const i18n = useI18n();
    const viewjobOneGraphModel = useViewjobOneGraphModel();

    const isDesktop = useIsDesktop();
    const scrollViewRef = useRef<ScrollViewRef>(null);
    const scrollOffsetXRef = useRef(0);
    const layoutWidthRef = useRef(0);
    const contentWidthRef = useRef(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const updateScrollState = useCallback(() => {
        setCanScrollPrev(scrollOffsetXRef.current > 0);
        setCanScrollNext(
            scrollOffsetXRef.current + layoutWidthRef.current < contentWidthRef.current - 1,
        );
    }, []);

    const handleScroll = useCallback(
        (event: NativeSyntheticEvent<NativeScrollEvent>) => {
            scrollOffsetXRef.current = event.nativeEvent.contentOffset.x;
            updateScrollState();
        },
        [updateScrollState],
    );

    const handleLayout = useCallback(
        (event: LayoutChangeEvent) => {
            layoutWidthRef.current = event.nativeEvent.layout.width;
            updateScrollState();
        },
        [updateScrollState],
    );

    const handleContentSizeChange = useCallback(
        (width: number) => {
            contentWidthRef.current = width;
            updateScrollState();
        },
        [updateScrollState],
    );

    const scrollByCards = useCallback((direction: 1 | -1) => {
        const target = Math.max(0, scrollOffsetXRef.current + direction * CARD_STRIDE);
        scrollViewRef.current?.scrollTo({ x: target, y: 0, animated: true });
        // Drop focus after a pointer-driven press so the browser's native :focus outline
        // doesn't linger. Keyboard-driven presses keep focus for accessibility.
        if (Platform.OS === 'web' && typeof document !== 'undefined') {
            const active = document.activeElement as HTMLElement | null;
            if (active && (!active.matches || !active.matches(':focus-visible'))) {
                active.blur?.();
            }
        }
    }, []);

    const companyName = viewjobOneGraphModel?.employer?.companyName;

    const { value: mobVjCompanyConquestingTstGrp } = useViewjobProctorAllocation(
        MobVjCompanyConquestingTst,
    );
    const jobLimit = BUCKET_TO_JOB_LIMIT[mobVjCompanyConquestingTstGrp];
    const mobVjCompanyConquestingTstActive = jobLimit !== undefined;
    const shouldShow =
        mobVjCompanyConquestingTstActive &&
        viewjobOneGraphModel?.job?.shouldClientSideLoadCompetitorsJobs;

    const { data, loading, error } = useGetViewJobRelevantJobsQuery({
        variables: {
            tag: 'jobKey',
            payload: jobKey,
            isLoggedIn: loggedIn,
            context: 'vjCompetitorsJobs',
            placementId: 'indeed-competitorjobs',
            limit: jobLimit,
        },
        skip: !shouldShow,
    });

    const jobCardsData = data?.findRelevantJobs?.results
        ? data?.findRelevantJobs?.results.map((result) => result.job)
        : [];

    useEffect(() => {
        if (loading || !shouldShow || !viewjobOneGraphModel?.mobvjtk) {
            return;
        }

        const isExpired = viewjobOneGraphModel.job?.isExpired;

        if (error) {
            logReceivedFlag({
                mobtk: viewjobOneGraphModel.mobvjtk,
                parentEventType: 'mobviewjob',
                flag: AvroOptional.string(
                    isExpired
                        ? 'expiredVjCompanyRecommendationsError'
                        : 'retargetedCompanyRecommendationsError',
                ),
                ts: AvroOptional.long(new Date().getTime()),
            });
        } else if (jobCardsData.length === 0) {
            logReceivedFlag({
                mobtk: viewjobOneGraphModel.mobvjtk,
                parentEventType: 'mobviewjob',
                flag: AvroOptional.string(
                    isExpired
                        ? 'expiredVjCompanyRecommendationsEmpty'
                        : 'retargetedCompanyRecommendationsEmpty',
                ),
                ts: AvroOptional.long(new Date().getTime()),
            });
        } else {
            logReceivedFlag({
                mobtk: viewjobOneGraphModel.mobvjtk,
                parentEventType: 'mobviewjob',
                flag: AvroOptional.string(
                    isExpired
                        ? 'expiredVjCompanyRecommendationsShown'
                        : 'retargetedCompanyRecommendationsShown',
                ),
                ts: AvroOptional.long(new Date().getTime()),
            });
        }
    }, [
        loading,
        shouldShow,
        error,
        jobCardsData.length,
        viewjobOneGraphModel?.mobvjtk,
        viewjobOneGraphModel?.job?.isExpired,
    ]);

    if (!shouldShow) {
        return null;
    }

    if (loading) {
        return <JobCardLoadingIndicator />;
    }

    return jobCardsData.length > 0 ? (
        <DislikedJobsProvider>
            <Box sx={styles.headerRow}>
                <Heading
                    level={4}
                    id="exploreOtherJobsHeading"
                    sx={{
                        color: colors.neutral['1000'],
                    }}
                >
                    {i18n.gettext('Explore other jobs')}
                </Heading>
                {isDesktop && jobCardsData.length > 1 && (
                    <Box sx={styles.navArrows}>
                        <Button
                            variant="tertiary"
                            iconOnly
                            size="sm"
                            ariaLabel={i18n.gettext('Previous')}
                            iconBefore={<ArrowLeft size="sm" variant="stroke" aria-hidden />}
                            onPress={() => scrollByCards(-1)}
                            disabled={!canScrollPrev}
                        />
                        <Button
                            variant="tertiary"
                            iconOnly
                            size="sm"
                            ariaLabel={i18n.gettext('Next')}
                            iconBefore={<ArrowRight size="sm" variant="stroke" aria-hidden />}
                            onPress={() => scrollByCards(1)}
                            disabled={!canScrollNext}
                        />
                    </Box>
                )}
            </Box>
            {companyName && (
                <Text
                    level={2}
                    variant="secondary"
                    sx={{
                        color: colors.neutral['800'],
                        fontWeight: 'normal',
                        marginBottom: 8,
                    }}
                >
                    {i18n.gettext('Discover opportunities beyond {0}.', [companyName])}
                </Text>
            )}
            <ScrollView
                ref={scrollViewRef}
                horizontal
                showsHorizontalScrollIndicator={false}
                sx={styles.container}
                scrollEventThrottle={16}
                onScroll={handleScroll}
                onLayout={handleLayout}
                onContentSizeChange={handleContentSizeChange}
            >
                {jobCardsData.map((job, index) => {
                    return (
                        <Box key={job.key} sx={styles.jobCardContainer}>
                            <JobCard
                                data={job}
                                viewJobFrom={'vjCompetitorsJobs'}
                                dislikeJobPageId={'coworker'}
                                containerStyle={{ width: CARD_WIDTH }}
                                webTargetUrl={prepareJobClickUrl(job.tracking?.jobClick?.url)}
                                onPressJobCard={(job: JobCardData) => {
                                    if (job.tracking) {
                                        const target = job.tracking?.jobClick?.url
                                            ? JSTViewJobTarget.url(job.tracking.jobClick.url)
                                            : JSTViewJobTarget.jobKey(job.key);
                                        navigateTo(
                                            JSTLandingScreen.viewJob(target),
                                            'rjp_mob.vjCompetitorsJobs',
                                        ).catch((err) =>
                                            console.error(
                                                'Failed to native navigate to View Job: ',
                                                err,
                                            ),
                                        );
                                    }
                                }}
                                onJobSaved={
                                    loggedIn
                                        ? () => {
                                              setVanishingBottomToastProps({
                                                  message: i18n.gettext('Job saved to My Jobs'),
                                                  variant: 'notification',
                                                  durationInMs: 3000,
                                              });
                                          }
                                        : undefined
                                }
                                DislikedJobCard={ViewJobDislikedJobCard}
                                loggedIn={loggedIn}
                                onJobCardImpression={
                                    index === 0
                                        ? () => {
                                              logReceivedFlag({
                                                  mobtk: viewjobOneGraphModel.mobvjtk,
                                                  parentEventType: 'mobviewjob',
                                                  flag: AvroOptional.string(
                                                      viewjobOneGraphModel.job?.isExpired
                                                          ? 'expiredVjCompanyRecommendationsSeen'
                                                          : 'retargetedCompanyRecommendationsSeen',
                                                  ),
                                                  ts: AvroOptional.long(new Date().getTime()),
                                              });
                                          }
                                        : undefined
                                }
                            />
                        </Box>
                    );
                })}
            </ScrollView>
            <ViewJobDivider />
        </DislikedJobsProvider>
    ) : null;
}

const styles = StyleSheet.create({
    jobCardContainer: {
        marginRight: 8,
        paddingRight: 16,
        display: 'flex',
        flexDirection: 'row',
        flexShrink: 0,
    },
    container: {
        paddingTop: 15,
        paddingBottom: 25,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 24,
        marginBottom: 4,
    },
    navArrows: {
        flexDirection: 'row',
        gap: 8,
    },
    showMoreContainer: {
        paddingRight: 16,
    },
    showMoreButton: {
        width: 243,
        alignSelf: 'flex-start',
    },
});
